<template>
  <div class="align-items-center justify-content-center" style="display: flex">
    <div style="width: 100%; max-width: 1100px; min-width: 320px">
      <h2 style="text-align: center; margin-bottom: 16px">
        Spieltage &amp; Spiele verwalten
      </h2>
      <h4 style="color: red; text-align: center" v-if="error && error.length > 0">
        {{ error }}
      </h4>
      <h4
        style="color: green; text-align: center"
        v-if="info && info.length > 0 && (!error || error.length === 0)"
      >
        {{ info }}
      </h4>

      <Stepper v-model:activeStep="activeStep">
        <!-- ===== Step 1: Saison ===== -->
        <StepperPanel header="Saison">
          <template #header="{ index, clickCallback }">
            <button
              class="p-stepper-action"
              role="tab"
              @click="navigateStep(index, clickCallback)"
            >
              <div class="flex flex-column align-items-center justify-content-center">
                <span class="p-stepper-number" style="width: 2rem">{{ index + 1 }}</span>
                <span class="p-stepper-title">Saison</span>
              </div>
            </button>
          </template>
          <template #content="{ nextCallback }">
            <div style="margin: 10px 0">
              <Dropdown
                v-model="selectedSeason"
                :options="seasons"
                optionLabel="representation"
                dataKey="id"
                placeholder="Bitte Saison auswählen"
                style="width: 80%"
                :filter="true"
                @change="onSeasonSelected"
              />
            </div>
            <div class="flex pt-4 justify-content-end">
              <Button
                label="Weiter"
                icon="pi pi-arrow-right"
                iconPos="right"
                :disabled="!selectedSeason || !selectedSeason.id"
                @click="nextCallback"
              />
            </div>
          </template>
        </StepperPanel>

        <!-- ===== Step 2: Liga ===== -->
        <StepperPanel header="Liga">
          <template #header="{ index, clickCallback }">
            <button
              class="p-stepper-action"
              role="tab"
              @click="navigateStep(index, clickCallback)"
            >
              <div class="flex flex-column align-items-center justify-content-center">
                <span class="p-stepper-number" style="width: 2rem">{{ index + 1 }}</span>
                <span class="p-stepper-title">Liga</span>
              </div>
            </button>
          </template>
          <template #content="{ prevCallback, nextCallback }">
            <div style="margin: 10px 0">
              <Dropdown
                v-model="selectedLeague"
                :options="availableLeagues"
                optionLabel="representation"
                dataKey="id"
                placeholder="Bitte Liga auswählen"
                style="width: 80%"
                :filter="true"
                @change="onLeagueSelected"
              />
            </div>
            <div class="flex pt-4 justify-content-between">
              <Button
                label="Zurück"
                severity="secondary"
                icon="pi pi-arrow-left"
                @click="prevCallback"
              />
              <Button
                label="Weiter"
                icon="pi pi-arrow-right"
                iconPos="right"
                :disabled="!selectedLeague || !selectedLeague.id"
                @click="nextCallback"
              />
            </div>
          </template>
        </StepperPanel>

        <!-- ===== Step 2: Spieltag ===== -->
        <StepperPanel header="Spieltag">
          <template #header="{ index, clickCallback }">
            <button
              class="p-stepper-action"
              role="tab"
              @click="navigateStep(index, clickCallback)"
            >
              <div class="flex flex-column align-items-center justify-content-center">
                <span class="p-stepper-number" style="width: 2rem">{{ index + 1 }}</span>
                <span class="p-stepper-title">Spieltag</span>
              </div>
            </button>
          </template>
          <template #content="{ prevCallback, nextCallback }">
            <div style="margin: 10px 0">
              <label style="display: block; margin-bottom: 4px">Spieltag wählen</label>
              <Dropdown
                v-model="selectedGameDayOption"
                :options="gameDayOptions"
                optionLabel="label"
                placeholder="– Spieltag auswählen –"
                style="width: 80%"
                @change="onGameDayOptionChanged"
              />
            </div>
            <div v-if="gameDayDraft" style="margin-top: 20px">
              <div class="field grid">
                <label class="col-fixed" style="width: 200px">Spieltag-Nummer*</label>
                <div class="col">
                  <InputNumber v-model="gameDayDraft.numberOfGameDay" :min="1" showButtons />
                </div>
              </div>
              <div class="field grid">
                <label class="col-fixed" style="width: 200px">Beginn (Datum)*</label>
                <div class="col">
                  <Calendar
                    v-model="gameDayDraft.gameDayStart"
                    dateFormat="dd.mm.yy"
                    :showIcon="true"
                  />
                </div>
              </div>
              <div class="field grid">
                <label class="col-fixed" style="width: 200px">Ende (Datum)*</label>
                <div class="col">
                  <Calendar
                    v-model="gameDayDraft.gameDayEnd"
                    dateFormat="dd.mm.yy"
                    :showIcon="true"
                  />
                </div>
              </div>
              <div class="field grid">
                <label class="col-fixed" style="width: 200px">Modus*</label>
                <div class="col">
                  <Dropdown
                    v-model="gameDayDraft.mode"
                    :options="gameDayModes"
                    optionLabel="label"
                    optionValue="value"
                    style="width: 200px"
                  />
                </div>
              </div>
              <div class="field grid">
                <label class="col-fixed" style="width: 200px">Beschreibung</label>
                <div class="col">
                  <Textarea
                    v-model="gameDayDraft.description"
                    rows="2"
                    style="width: 100%"
                  />
                </div>
              </div>
            </div>
            <div class="flex pt-4 justify-content-between" style="gap: 8px">
              <Button
                label="Zurück"
                severity="secondary"
                icon="pi pi-arrow-left"
                @click="prevCallback"
              />
              <span
                v-tooltip.top="canDeleteGameDay ? '' : 'Erst alle Spiele des Spieltags löschen'"
              >
                <Button
                  v-if="gameDayDraft && gameDayDraft.id"
                  label="Löschen"
                  severity="danger"
                  icon="pi pi-trash"
                  :disabled="!canDeleteGameDay"
                  @click="deleteGameDayDialog = true"
                />
              </span>
              <Button
                label="Speichern & Weiter"
                icon="pi pi-arrow-right"
                iconPos="right"
                :disabled="!gameDayDraftValid"
                @click="saveGameDay(nextCallback)"
              />
            </div>
          </template>
        </StepperPanel>

        <!-- ===== Step 3: Spiele ===== -->
        <StepperPanel header="Spiele">
          <template #header="{ index, clickCallback }">
            <button
              class="p-stepper-action"
              role="tab"
              @click="navigateStep(index, clickCallback)"
            >
              <div class="flex flex-column align-items-center justify-content-center">
                <span class="p-stepper-number" style="width: 2rem">{{ index + 1 }}</span>
                <span class="p-stepper-title">Spiele</span>
              </div>
            </button>
          </template>
          <template #content="{ prevCallback }">
            <h4 style="margin: 6px 0">Bestehende Spiele dieses Spieltags</h4>
            <DataTable
              class="p-datatable-sm"
              :value="gameDayDraft && gameDayDraft.games ? gameDayDraft.games : []"
              stripedRows
              responsiveLayout="stack"
              breakpoint="550px"
              style="width: 100%; margin-bottom: 16px"
            >
              <Column header="Datum">
                <template #body="{ data }">{{ formatDateTimeDisplay(data.date) }}</template>
              </Column>
              <Column header="Heim">
                <template #body="{ data }">{{ data.teamHome ? data.teamHome.name : '–' }}</template>
              </Column>
              <Column header="Gast">
                <template #body="{ data }">{{ data.teamGuest ? data.teamGuest.name : '–' }}</template>
              </Column>
              <Column header="Spielort">
                <template #body="{ data }">{{ data.location ? data.location.name : '–' }}</template>
              </Column>
              <Column header="Aktionen">
                <template #body="{ data }">
                  <Button
                    icon="pi pi-pencil"
                    class="p-button-rounded p-button-success"
                    style="margin: 2px"
                    v-tooltip.top="'Bearbeiten'"
                    @click="editGame(data)"
                  />
                  <Button
                    icon="pi pi-trash"
                    class="p-button-rounded p-button-warning"
                    style="margin: 2px"
                    v-tooltip.top="'Löschen'"
                    @click="askDeleteGame(data)"
                  />
                </template>
              </Column>
            </DataTable>

            <h4 style="margin: 6px 0">
              {{ gameDraft.id ? 'Spiel bearbeiten' : 'Neues Spiel anlegen' }}
            </h4>
            <p style="margin: 0 0 10px 0; color: #666" v-if="!gameDraft.id">
              Nach dem Speichern bleibt dieser Spieltag aktiv – es kann direkt
              das nächste Spiel erfasst werden.
            </p>
            <div class="field grid">
              <label class="col-fixed" style="width: 200px">Heim-Mannschaft*</label>
              <div class="col">
                <Dropdown
                  v-model="gameDraft.teamHome"
                  :options="availableHomeTeams"
                  optionLabel="name"
                  dataKey="id"
                  placeholder="Heim wählen"
                  :filter="true"
                  style="width: 100%"
                  @change="onTeamHomeChanged"
                />
              </div>
            </div>
            <div class="field grid">
              <label class="col-fixed" style="width: 200px">Gast-Mannschaft*</label>
              <div class="col">
                <Dropdown
                  v-model="gameDraft.teamGuest"
                  :options="availableGuestTeams"
                  optionLabel="name"
                  dataKey="id"
                  placeholder="Gast wählen"
                  :filter="true"
                  style="width: 100%"
                />
              </div>
            </div>
            <div class="field grid">
              <label class="col-fixed" style="width: 200px">Datum &amp; Uhrzeit*</label>
              <div class="col">
                <Calendar
                  v-model="gameDraft.date"
                  dateFormat="dd.mm.yy"
                  :showTime="true"
                  hourFormat="24"
                  :showIcon="true"
                />
              </div>
            </div>
            <div class="field grid">
              <label class="col-fixed" style="width: 200px">Spielort*</label>
              <div class="col">
                <Dropdown
                  v-model="gameDraft.location"
                  :options="locations"
                  optionLabel="representation"
                  dataKey="id"
                  placeholder="Spielort wählen"
                  :filter="true"
                  :filterFields="['name', 'city', 'street']"
                  style="width: 100%"
                  @change="onLocationChanged"
                />
              </div>
            </div>

            <div class="flex pt-4 justify-content-between" style="gap: 8px; flex-wrap: wrap">
              <Button
                label="Zurück"
                severity="secondary"
                icon="pi pi-arrow-left"
                @click="prevCallback"
              />
              <Button
                :label="gameDraft.id ? 'Neues Spiel' : 'Eingaben leeren'"
                severity="secondary"
                icon="pi pi-plus"
                @click="newGameDraft"
              />
              <Button
                :label="gameDraft.id ? 'Speichern' : 'Spiel anlegen'"
                icon="pi pi-save"
                :disabled="!gameDraftValid"
                @click="saveGame"
              />
              <Button
                label="Weiterer Spieltag"
                severity="secondary"
                icon="pi pi-calendar-plus"
                @click="startNextGameDay"
              />
              <Button
                label="Fertig"
                severity="success"
                icon="pi pi-check"
                @click="resetWizard"
              />
            </div>
          </template>
        </StepperPanel>
      </Stepper>
    </div>

    <!-- Confirm Dialog: GameDay -->
    <Dialog v-model:visible="deleteGameDayDialog" :closable="false">
      <template #header>
        <h3>Spieltag löschen</h3>
      </template>
      Soll der Spieltag „{{ gameDayDraft && gameDayDraft.numberOfGameDay }}" wirklich
      gelöscht werden?
      <template #footer>
        <Button
          label="Abbrechen"
          icon="pi pi-times"
          severity="secondary"
          @click="deleteGameDayDialog = false"
        />
        <Button
          label="Löschen"
          icon="pi pi-trash"
          severity="danger"
          @click="deleteGameDayConfirmed"
        />
      </template>
    </Dialog>

    <!-- Confirm Dialog: Game -->
    <Dialog v-model:visible="deleteGameDialog" :closable="false">
      <template #header>
        <h3>Spiel löschen</h3>
      </template>
      Soll dieses Spiel wirklich gelöscht werden?
      <template #footer>
        <Button
          label="Abbrechen"
          icon="pi pi-times"
          severity="secondary"
          @click="
            deleteGameDialog = false;
            gameToDelete = null;
          "
        />
        <Button
          label="Löschen"
          icon="pi pi-trash"
          severity="danger"
          @click="deleteGameConfirmed"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import ServerConnection from "../../services/ServerConnection/ServerConnection";
import { useMenuService } from "../../services/MenuService/UseMenuService";
import Stepper from "primevue/stepper";
import StepperPanel from "primevue/stepperpanel";
import Button from "primevue/button";
import Dropdown from "primevue/dropdown";
import InputNumber from "primevue/inputnumber";
import Textarea from "primevue/textarea";
import Calendar from "primevue/calendar";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Dialog from "primevue/dialog";

const EMPTY_GAME_DAY = {
  id: null,
  numberOfGameDay: 1,
  gameDayStart: null,
  gameDayEnd: null,
  mode: "REGULAR",
  description: "",
  league: null,
  location: null,
  games: [],
};

const EMPTY_GAME = {
  id: null,
  date: null,
  teamHome: null,
  teamGuest: null,
  location: null,
  gameDay: null,
  gameStatus: "ToPlay",
};

export default {
  name: "GameAndGameDayWizard",
  components: {
    Stepper,
    StepperPanel,
    Button,
    Dropdown,
    InputNumber,
    Textarea,
    Calendar,
    DataTable,
    Column,
    Dialog,
  },
  data() {
    return {
      activeStep: 0,
      error: "",
      info: "",
      seasons: [],
      selectedSeason: null,
      selectedLeague: null,
      gameDays: [],
      selectedGameDayOption: null,
      gameDayDraft: { ...EMPTY_GAME_DAY },
      gameDayRaw: null,
      gameDayModes: [
        { label: "Regulär", value: "REGULAR" },
        { label: "Block", value: "BLOCK" },
      ],
      locations: [],
      gameDraft: { ...EMPTY_GAME },
      locationManuallyChanged: false,
      deleteGameDayDialog: false,
      deleteGameDialog: false,
      gameToDelete: null,
    };
  },
  computed: {
    availableLeagues() {
      if (!this.selectedSeason || !Array.isArray(this.selectedSeason.leagues)) {
        return [];
      }
      return [...this.selectedSeason.leagues].sort((a, b) =>
        (a.representation || a.name || "").localeCompare(
          b.representation || b.name || ""
        )
      );
    },
    gameDayOptions() {
      var newEntry = { label: "– Neuer Spieltag –", gameDay: null };
      var existing = this.gameDays.map((gd) => ({
        label:
          "Spieltag " +
          (gd.numberOfGameDay != null ? gd.numberOfGameDay : "?") +
          (gd.gameDayStart
            ? " (" + this.formatDateDisplay(gd.gameDayStart) + ")"
            : ""),
        gameDay: gd,
      }));
      return [newEntry, ...existing];
    },
    availableTeams() {
      var fromLeague =
        this.selectedLeague && Array.isArray(this.selectedLeague.teams)
          ? [...this.selectedLeague.teams]
          : [];
      // Defensiv: falls das aktuell bearbeitete Spiel ein Team referenziert,
      // das nicht (mehr) in der Liga-Teamsliste steht, trotzdem auswählbar lassen.
      [this.gameDraft.teamHome, this.gameDraft.teamGuest].forEach((t) => {
        if (t && t.id != null && !fromLeague.some((x) => x.id === t.id)) {
          fromLeague.push(t);
        }
      });
      return fromLeague.sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      );
    },
    availableHomeTeams() {
      if (!this.gameDraft.teamGuest) {
        return this.availableTeams;
      }
      return this.availableTeams.filter(
        (t) => t.id !== this.gameDraft.teamGuest.id
      );
    },
    availableGuestTeams() {
      if (!this.gameDraft.teamHome) {
        return this.availableTeams;
      }
      return this.availableTeams.filter(
        (t) => t.id !== this.gameDraft.teamHome.id
      );
    },
    gameDayDraftValid() {
      var d = this.gameDayDraft;
      return (
        d &&
        d.numberOfGameDay != null &&
        d.numberOfGameDay > 0 &&
        d.gameDayStart &&
        d.gameDayEnd &&
        d.mode
      );
    },
    canDeleteGameDay() {
      return (
        this.gameDayDraft &&
        this.gameDayDraft.id &&
        (!this.gameDayDraft.games || this.gameDayDraft.games.length === 0)
      );
    },
    gameDraftValid() {
      var g = this.gameDraft;
      return (
        g &&
        g.teamHome &&
        g.teamGuest &&
        g.teamHome.id !== g.teamGuest.id &&
        g.date &&
        g.location
      );
    },
  },
  mounted() {
    const menuService = useMenuService();
    menuService.setMainMenuItemNames(["home"]);
    this.loadSeasons();
    this.loadLocations();
  },
  methods: {
    navigateStep(targetIndex, clickCallback) {
      if (targetIndex === this.activeStep) {
        return;
      }
      if (targetIndex > this.activeStep) {
        if (targetIndex >= 1 && (!this.selectedSeason || !this.selectedSeason.id)) {
          this.error = "Bitte zuerst eine Saison auswählen.";
          return;
        }
        if (targetIndex >= 2 && (!this.selectedLeague || !this.selectedLeague.id)) {
          this.error = "Bitte zuerst eine Liga auswählen.";
          return;
        }
        if (targetIndex >= 3 && !this.gameDayDraft.id) {
          this.error = "Bitte zuerst den Spieltag speichern.";
          return;
        }
      }
      this.error = "";
      clickCallback();
    },
    loadSeasons() {
      ServerConnection.get("data/season")
        .then((response) => {
          var list = Array.isArray(response.data) ? response.data : [];
          list.sort((a, b) =>
            (a.representation || a.name || "").localeCompare(
              b.representation || b.name || ""
            )
          );
          this.seasons = list;
          var current = list.find((s) => s.currentSeason === true);
          if (current && !this.selectedSeason) {
            this.selectedSeason = current;
            this.onSeasonSelected();
          }
        })
        .catch((error) => {
          this.error = "Konnte Saisons nicht laden: " + this.errorMessage(error);
        });
    },
    onSeasonSelected() {
      this.error = "";
      this.info = "";
      this.selectedLeague = null;
      this.selectedGameDayOption = null;
      this.gameDays = [];
      this.gameDayDraft = { ...EMPTY_GAME_DAY };
      this.gameDayRaw = null;
      this.resetGameDraft();
    },
    loadLocations() {
      ServerConnection.get("data/location")
        .then((response) => {
          var list = Array.isArray(response.data) ? response.data : [];
          list.sort((a, b) =>
            (a.representation || a.name || "").localeCompare(
              b.representation || b.name || ""
            )
          );
          this.locations = list;
        })
        .catch((error) => {
          this.error =
            "Konnte Spielorte nicht laden: " + this.errorMessage(error);
        });
    },
    onLeagueSelected() {
      this.error = "";
      this.info = "";
      this.selectedGameDayOption = null;
      this.gameDayDraft = { ...EMPTY_GAME_DAY };
      this.gameDayRaw = null;
      this.resetGameDraft();
      this.refreshLeagueDetail();
    },
    refreshLeagueDetail() {
      if (!this.selectedLeague || !this.selectedLeague.id) {
        return Promise.resolve();
      }
      var leagueId = this.selectedLeague.id;
      return ServerConnection.get("data/league/" + leagueId)
        .then((response) => {
          if (!this.selectedLeague || this.selectedLeague.id !== leagueId) {
            return;
          }
          this.selectedLeague = response.data;
          var gameDays = Array.isArray(response.data && response.data.gameDays)
            ? response.data.gameDays
            : [];
          this.gameDays = [...gameDays].sort(
            (a, b) => (a.numberOfGameDay || 0) - (b.numberOfGameDay || 0)
          );
        })
        .catch((error) => {
          this.error =
            "Konnte Liga-Detail nicht laden: " + this.errorMessage(error);
        });
    },
    onGameDayOptionChanged() {
      this.error = "";
      this.info = "";
      var option = this.selectedGameDayOption;
      if (!option || option.gameDay === null) {
        this.gameDayRaw = null;
        this.gameDayDraft = {
          ...EMPTY_GAME_DAY,
          numberOfGameDay: this.nextGameDayNumber(),
          league: { id: this.selectedLeague.id },
          games: [],
        };
        this.resetGameDraft();
        return;
      }
      // Der Spieltag aus dem Liga-Detail enthält die Spiele nur ohne
      // Mannschaften/Spielort – daher den vollen Datensatz nachladen.
      this.reloadGameDay(option.gameDay.id)
        .then(() => this.resetGameDraft())
        .catch((error) => {
          this.error =
            "Konnte Spieltag nicht laden: " + this.errorMessage(error);
        });
    },
    nextGameDayNumber() {
      if (!this.gameDays || this.gameDays.length === 0) {
        return 1;
      }
      var max = 0;
      this.gameDays.forEach((gd) => {
        if (gd.numberOfGameDay && gd.numberOfGameDay > max) {
          max = gd.numberOfGameDay;
        }
      });
      return max + 1;
    },
    saveGameDay(nextCallback) {
      this.error = "";
      this.info = "";
      var d = this.gameDayDraft;
      var payload = {
        id: d.id,
        numberOfGameDay: d.numberOfGameDay,
        gameDayStart: this.formatLocalDate(d.gameDayStart),
        gameDayEnd: this.formatLocalDate(d.gameDayEnd),
        mode: d.mode,
        description: d.description,
        league: { id: this.selectedLeague.id },
        location: d.location || null,
      };
      var promise;
      if (d.id) {
        promise = ServerConnection.put(
          "data/gameday/" + d.id,
          JSON.stringify(payload)
        );
      } else {
        promise = ServerConnection.post("data/gameday", payload);
      }
      promise
        .then((response) => {
          var newId = d.id;
          if (response && response.data && response.data.id) {
            newId = response.data.id;
          }
          return this.reloadGameDay(newId);
        })
        .then(() => this.refreshLeagueDetail())
        .then(() => {
          if (typeof nextCallback === "function") {
            nextCallback();
          }
          this.resetGameDraft();
        })
        .catch((error) => {
          this.error =
            "Speichern fehlgeschlagen: " + this.errorMessage(error);
        });
    },
    reloadGameDay(id) {
      if (!id) {
        return Promise.resolve();
      }
      return ServerConnection.get("data/gameday/" + id).then((response) => {
        var gd = response.data;
        this.gameDayRaw = gd;
        this.gameDayDraft = {
          id: gd.id,
          numberOfGameDay: gd.numberOfGameDay,
          gameDayStart: gd.gameDayStart ? new Date(gd.gameDayStart) : null,
          gameDayEnd: gd.gameDayEnd ? new Date(gd.gameDayEnd) : null,
          mode: gd.mode || "REGULAR",
          description: gd.description || "",
          league: gd.league,
          location: gd.location || null,
          games: Array.isArray(gd.games) ? gd.games : [],
        };
        // Keep dropdown selection in sync
        var match = this.gameDayOptions.find(
          (opt) => opt.gameDay && opt.gameDay.id === gd.id
        );
        if (match) {
          this.selectedGameDayOption = match;
        }
      });
    },
    deleteGameDayConfirmed() {
      this.deleteGameDayDialog = false;
      var d = this.gameDayDraft;
      if (!d || !d.id) {
        return;
      }
      ServerConnection.delete("data/gameday/" + d.id)
        .then(() => {
          this.gameDayDraft = { ...EMPTY_GAME_DAY };
          this.gameDayRaw = null;
          this.selectedGameDayOption = null;
          this.resetGameDraft();
          this.refreshLeagueDetail();
        })
        .catch((error) => {
          this.error =
            "Löschen fehlgeschlagen: " + this.errorMessage(error);
        });
    },
    onTeamHomeChanged() {
      if (
        !this.locationManuallyChanged &&
        this.gameDraft.teamHome &&
        this.gameDraft.teamHome.location
      ) {
        this.gameDraft.location = this.gameDraft.teamHome.location;
      }
    },
    onLocationChanged() {
      this.locationManuallyChanged = true;
    },
    resetGameDraft(preferredDate) {
      var defaultDate = preferredDate ? new Date(preferredDate) : null;
      if (!defaultDate && this.gameDayDraft.gameDayStart) {
        defaultDate = new Date(this.gameDayDraft.gameDayStart);
        defaultDate.setHours(19, 0, 0, 0);
      }
      this.gameDraft = {
        ...EMPTY_GAME,
        date: defaultDate,
        gameDay: this.gameDayDraft.id ? { id: this.gameDayDraft.id } : null,
      };
      this.locationManuallyChanged = false;
    },
    // Eigener Handler, damit das Click-Event nicht als Datum ankommt.
    newGameDraft() {
      this.error = "";
      this.info = "";
      this.resetGameDraft();
    },
    editGame(game) {
      this.error = "";
      this.info = "";
      this.gameDraft = {
        id: game.id,
        date: game.date ? new Date(game.date) : null,
        teamHome: this.resolveTeamReference(game.teamHome),
        teamGuest: this.resolveTeamReference(game.teamGuest),
        location: this.resolveLocationReference(game.location),
        gameDay: { id: this.gameDayDraft.id },
        gameStatus: game.gameStatus || "ToPlay",
      };
      // Existing game: don't auto-overwrite the location on team-home change
      this.locationManuallyChanged = true;
    },
    resolveTeamReference(team) {
      if (!team || team.id == null) {
        return null;
      }
      var match = this.availableTeams.find((t) => t.id === team.id);
      return match || team;
    },
    resolveLocationReference(location) {
      if (!location || location.id == null) {
        return null;
      }
      var match = this.locations.find((l) => l.id === location.id);
      return match || location;
    },
    // Die Entity-Plugins auf Serverseite lesen gameDay.gameDayStart/-End und
    // gameDay.league.leagueType. Deshalb muss der Spieltag so zurückgeschickt
    // werden, wie ihn der Server geliefert hat – eine reine {id}-Referenz
    // führt dort zu einer NullPointerException.
    buildGameDayReference() {
      if (this.gameDayRaw && this.gameDayRaw.id === this.gameDayDraft.id) {
        var reference = { ...this.gameDayRaw };
        delete reference.games;
        return reference;
      }
      return this.gameDayDraft.id ? { id: this.gameDayDraft.id } : null;
    },
    saveGame() {
      this.error = "";
      this.info = "";
      var g = this.gameDraft;
      var wasUpdate = !!g.id;
      var savedDate = g.date ? new Date(g.date) : null;
      var payload = {
        id: g.id,
        date: this.formatLocalDateTime(g.date),
        teamHome: g.teamHome ? { id: g.teamHome.id } : null,
        teamGuest: g.teamGuest ? { id: g.teamGuest.id } : null,
        location: g.location ? { id: g.location.id } : null,
        gameDay: this.buildGameDayReference(),
        gameStatus: g.gameStatus || "ToPlay",
      };
      var promise;
      if (g.id) {
        promise = ServerConnection.put(
          "data/game/" + g.id,
          JSON.stringify(payload)
        );
      } else {
        promise = ServerConnection.post("data/game", payload);
      }
      promise
        .then(() => this.reloadGameDay(this.gameDayDraft.id))
        .then(() => {
          // Direkt weiter mit dem nächsten Spiel desselben Spieltags:
          // Datum/Uhrzeit des gerade gespeicherten Spiels bleibt vorbelegt.
          this.resetGameDraft(savedDate);
          this.info = wasUpdate
            ? "Spiel gespeichert."
            : "Spiel angelegt. Weiteres Spiel für diesen Spieltag kann direkt erfasst werden.";
        })
        .catch((error) => {
          this.error =
            "Speichern fehlgeschlagen: " + this.errorMessage(error);
        });
    },
    askDeleteGame(game) {
      this.gameToDelete = game;
      this.deleteGameDialog = true;
    },
    deleteGameConfirmed() {
      this.deleteGameDialog = false;
      if (!this.gameToDelete) {
        return;
      }
      var id = this.gameToDelete.id;
      this.gameToDelete = null;
      ServerConnection.delete("data/game/" + id)
        .then(() => this.reloadGameDay(this.gameDayDraft.id))
        .then(() => {
          if (this.gameDraft.id === id) {
            this.resetGameDraft();
          }
        })
        .catch((error) => {
          this.error =
            "Löschen fehlgeschlagen: " + this.errorMessage(error);
        });
    },
    resetWizard() {
      this.activeStep = 0;
      this.error = "";
      this.info = "";
      this.selectedLeague = null;
      this.gameDays = [];
      this.selectedGameDayOption = null;
      this.gameDayDraft = { ...EMPTY_GAME_DAY };
      this.gameDayRaw = null;
      this.resetGameDraft();
      // selectedSeason bewusst stehen lassen, damit der Admin nicht
      // jedes Mal die Saison neu wählen muss.
    },
    // Nächster Spieltag derselben Liga: zurück auf Schritt "Spieltag",
    // Saison und Liga bleiben gewählt.
    startNextGameDay() {
      this.error = "";
      this.info = "";
      this.selectedGameDayOption = null;
      this.gameDayDraft = { ...EMPTY_GAME_DAY };
      this.gameDayRaw = null;
      this.resetGameDraft();
      this.refreshLeagueDetail();
      this.activeStep = 2;
    },
    errorMessage(error) {
      if (error && error.response && error.response.data) {
        return typeof error.response.data === "string"
          ? error.response.data
          : JSON.stringify(error.response.data);
      }
      if (error && error.message) {
        return error.message;
      }
      return "Unbekannter Fehler";
    },
    formatLocalDate(d) {
      if (!d) {
        return null;
      }
      var date = d instanceof Date ? d : new Date(d);
      var y = date.getFullYear();
      var m = String(date.getMonth() + 1).padStart(2, "0");
      var dd = String(date.getDate()).padStart(2, "0");
      return y + "-" + m + "-" + dd;
    },
    formatLocalDateTime(d) {
      if (!d) {
        return null;
      }
      var date = d instanceof Date ? d : new Date(d);
      var y = date.getFullYear();
      var m = String(date.getMonth() + 1).padStart(2, "0");
      var dd = String(date.getDate()).padStart(2, "0");
      var h = String(date.getHours()).padStart(2, "0");
      var mi = String(date.getMinutes()).padStart(2, "0");
      // Das Backend deserialisiert LocalDateTime mit dem Muster "yyyy-MM-dd HH:mm".
      return y + "-" + m + "-" + dd + " " + h + ":" + mi;
    },
    formatDateDisplay(d) {
      if (!d) {
        return "";
      }
      var date = d instanceof Date ? d : new Date(d);
      return date.toLocaleDateString("de-DE");
    },
    formatDateTimeDisplay(d) {
      if (!d) {
        return "";
      }
      var date = d instanceof Date ? d : new Date(d);
      return (
        date.toLocaleDateString("de-DE") +
        " " +
        date.toLocaleTimeString("de-DE", {
          hour: "2-digit",
          minute: "2-digit",
        })
      );
    },
  },
};
</script>
